import React from "react";
import {
  ArrowRight,
  BrainCircuit,
  ChevronDown,
  Dna,
  Eye,
  HeartHandshake,
  Lightbulb,
  Repeat2,
  ShieldCheck,
  Sparkles,
  Waves,
} from "lucide-react";
import { buildRobustNervousSystemProfile } from "@/components/assessment/robustAssessmentEngine";
import { buildRobustClientNarrative } from "@/components/assessment/robustResultNarrative";

const BulletList = ({ items }) => (
  <ul className="mt-4 space-y-3">
    {(items || []).map((item) => (
      <li key={item} className="flex gap-3 text-sm leading-relaxed forr-text-soft">
        <span className="mt-0.5 forr-text-teal-deep">-</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const StoryCard = ({ number, title, body }) => (
  <article
    className="rounded-[22px] p-5 forr-surface sm:p-6"
    style={{ border: "1px solid var(--forr-line-soft)" }}
  >
    <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
      {number}
    </div>
    <h3 className="mt-2 font-display text-xl forr-text-ink">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed forr-text-soft">{body}</p>
  </article>
);

const SignalChips = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {(items || []).map((item) => (
      <span
        key={item}
        className="rounded-full px-3 py-1.5 text-xs forr-text-ink"
        style={{ backgroundColor: "var(--forr-teal-tint)" }}
      >
        {item}
      </span>
    ))}
  </div>
);

const SystemList = ({ items }) => (
  <ul className="mt-3 space-y-3">
    {(items || []).map((item) => (
      <li key={item.name} className="text-xs leading-relaxed forr-text-soft">
        <span className="font-semibold forr-text-ink">{item.name}:</span>{" "}
        {item.role}
      </li>
    ))}
  </ul>
);

const ExpressionDisclosure = ({ section }) => (
  <details
    className="group overflow-hidden rounded-[22px] bg-white/75 transition-colors open:bg-white"
    data-result-disclosure={section.key}
  >
    <summary className="flex cursor-pointer list-none items-start justify-between gap-4 p-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-teal-700 sm:p-6">
      <div className="min-w-0">
        <div className="forr-overline">{section.kicker}</div>
        <h3 className="mt-2 font-display text-2xl forr-text-ink">
          {section.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed forr-text-soft">
          {section.summary}
        </p>
        <div className="mt-4">
          <SignalChips items={section.signals} />
        </div>
      </div>
      <span
        className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white forr-text-teal-deep"
        aria-hidden="true"
      >
        <ChevronDown
          size={18}
          className="transition-transform duration-200 group-open:rotate-180"
        />
      </span>
    </summary>
    <div
      className="border-t px-5 pb-6 pt-5 sm:px-6"
      style={{ borderColor: "var(--forr-line-soft)" }}
      data-result-disclosure-content
    >
      <div className="forr-overline">Why this may happen</div>
      <p className="mt-3 text-sm leading-relaxed forr-text-ink">
        {section.explanation}
      </p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div className="rounded-2xl p-4 forr-surface-tint">
          <div className="forr-overline">Systems involved</div>
          <SystemList items={section.systems} />
        </div>
        <div className="rounded-2xl p-4 forr-surface-tint">
          <div className="forr-overline">Questions worth asking yourself</div>
          <BulletList items={section.questions} />
        </div>
      </div>

      <div className="mt-5 rounded-2xl p-4 forr-panel-bottom-line">
        <div className="forr-overline">What could help in these situations</div>
        <BulletList items={section.supports} />
      </div>

      {section.safetyNote && (
        <p className="mt-4 text-xs leading-relaxed forr-text-mute">
          {section.safetyNote}
        </p>
      )}
    </div>
  </details>
);

const ScienceCard = ({ module, icon: Icon }) => (
  <article
    className="rounded-[24px] p-5 forr-surface sm:p-6"
    style={{ border: "1px solid var(--forr-line-soft)" }}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="forr-overline">How change becomes possible</div>
        <h3 className="mt-2 font-display text-2xl forr-text-ink">
          {module.title}
        </h3>
      </div>
      <Icon size={24} className="shrink-0 forr-text-teal-deep" />
    </div>
    <p className="mt-4 text-sm leading-relaxed forr-text-soft">
      {module.science}
    </p>
    <p className="mt-3 text-sm leading-relaxed forr-text-soft">
      {module.biology}
    </p>
    <div className="mt-4 rounded-2xl p-4 forr-panel-information">
      <div className="forr-overline">Why this matters for your pattern</div>
      <p className="mt-2 text-sm leading-relaxed forr-text-ink">
        {module.profileRelevance}
      </p>
      <p className="mt-3 text-sm leading-relaxed forr-text-soft">
        {module.homeostasis}
      </p>
    </div>
    <div className="mt-4 rounded-2xl p-4 forr-surface-tint">
      <div className="forr-overline">Bottom line</div>
      <p className="mt-2 text-sm leading-relaxed forr-text-ink">
        {module.bottomLine}
      </p>
    </div>
    <details className="mt-4 text-xs forr-text-mute">
      <summary className="cursor-pointer font-medium forr-text-teal-deep">
        Science references
      </summary>
      <ul className="mt-3 space-y-2 pl-4">
        {module.sources.map((source) => (
          <li key={source}>{source}</li>
        ))}
      </ul>
    </details>
  </article>
);

const RobustAssessmentProfileView = ({ answers, profile: suppliedProfile }) => {
  const profile = suppliedProfile || buildRobustNervousSystemProfile(answers);
  const narrative = buildRobustClientNarrative(profile);

  return (
    <div className="space-y-6" data-testid="robust-assessment-profile-view">
      <section className="overflow-hidden rounded-[30px] forr-panel-action">
        <div className="p-6 sm:p-9">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="forr-overline">Your Adaptive Physiology profile</div>
              <h1 className="mt-3 max-w-4xl font-display text-4xl font-medium leading-[1.08] tracking-[-0.03em] forr-text-ink sm:text-5xl">
                {profile.headline}
              </h1>
            </div>
            <BrainCircuit size={30} className="shrink-0 forr-text-teal-deep" />
          </div>
          <div className="mt-7 rounded-[22px] bg-white/75 p-5 sm:p-6">
            <div className="forr-overline">What your responses suggest</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              {narrative.stateName}
            </h2>
            <p className="mt-3 text-base leading-relaxed forr-text-ink">
              {narrative.stateDescription}
            </p>
            <p className="mt-4 text-sm leading-relaxed forr-text-soft">
              {profile.bottomLine}
            </p>
          </div>
        </div>
        <div className="border-t border-white/60 bg-white/35 px-6 py-4 sm:px-9">
          <p className="text-xs leading-relaxed forr-text-soft">
            This is not a label for who you are. It is a working explanation of
            how your nervous system may organize demand, choice, emotion, and
            recovery under your current conditions.
          </p>
        </div>
      </section>

      <section>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">A look inside your response</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink sm:text-4xl">
              How your nervous system may be working
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed forr-text-soft">
              Your reaction is not one score. It is the interaction between how
              quickly you mobilize, how much choice remains, how recovery occurs,
              and what your environment and resources are asking of you.
            </p>
          </div>
          <Waves size={25} className="shrink-0 forr-text-teal-deep" />
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {narrative.nervousSystemStory.map((item, index) => (
            <StoryCard
              key={item.key}
              number={`0${index + 1}`}
              title={item.title}
              body={item.body}
            />
          ))}
        </div>
      </section>

      <section className="rounded-[26px] p-6 forr-panel-bottom-line sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">Why your system may be functioning this way</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink">
              The pattern may be protective, learned, and context-dependent
            </h2>
          </div>
          <ShieldCheck size={25} className="shrink-0 forr-text-teal-deep" />
        </div>
        <div className="mt-5 space-y-4">
          {narrative.whyThisMayBeHappening.map((paragraph) => (
            <p key={paragraph} className="text-sm leading-relaxed forr-text-ink">
              {paragraph}
            </p>
          ))}
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-[20px] bg-white/65 p-5">
            <div className="forr-overline">What may be adding demand</div>
            <BulletList items={narrative.reportedDemand} />
          </div>
          <div className="rounded-[20px] bg-white/65 p-5">
            <div className="forr-overline">What may already support you</div>
            <BulletList items={narrative.reportedSupport} />
          </div>
        </div>
      </section>

      <section className="rounded-[26px] p-6 forr-panel-information sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">How this may show up in daily life</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink">
              Emotion, body, thinking, and behaviour are parts of one response
            </h2>
            <div className="mt-4 max-w-4xl space-y-3">
              {narrative.dailyLife.overview.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-sm leading-relaxed forr-text-ink"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
          <Eye size={25} className="shrink-0 forr-text-teal-deep" />
        </div>

        <div className="mt-5 rounded-[20px] bg-white/65 p-5">
          <div className="forr-overline">Why change is still possible</div>
          <p className="mt-3 text-sm leading-relaxed forr-text-soft">
            {narrative.dailyLife.plasticitySnippet}
          </p>
        </div>

        <p className="mt-5 text-xs leading-relaxed forr-text-mute">
          Open any section for the biological connection, the specific systems
          involved, questions to consider, and practical support options.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {narrative.dailyLife.sections.map((section) => (
            <ExpressionDisclosure key={section.key} section={section} />
          ))}
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <details className="group rounded-[20px] bg-white/60 p-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700">
              <span className="forr-overline">Other ways this pattern can appear</span>
              <ChevronDown
                size={17}
                className="shrink-0 transition-transform duration-200 group-open:rotate-180 forr-text-teal-deep"
                aria-hidden="true"
              />
            </summary>
            <BulletList items={narrative.dailyLife.possiblePatterns} />
          </details>

          <details className="group rounded-[20px] bg-white/60 p-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700">
              <span className="forr-overline">Specific systems and research terms</span>
              <ChevronDown
                size={17}
                className="shrink-0 transition-transform duration-200 group-open:rotate-180 forr-text-teal-deep"
                aria-hidden="true"
              />
            </summary>
            <p className="mt-4 text-xs leading-relaxed forr-text-soft">
              {narrative.dailyLife.vagalClarifier}
            </p>
            <div className="mt-4 forr-overline">Useful starting points</div>
            <BulletList items={narrative.dailyLife.researchStartingPoints} />
            <div className="mt-5 forr-overline">Research references</div>
            <BulletList items={narrative.dailyLife.sources} />
          </details>
        </div>
      </section>

      <section>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">Your nervous system can adapt</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink sm:text-4xl">
              Why repeated physical input can change the pattern
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed forr-text-soft">
              The goal is not to force a feeling away. It is to give your nervous
              system a matched experience it can complete, recover from, and learn
              from repeatedly.
            </p>
          </div>
          <Dna size={25} className="shrink-0 forr-text-teal-deep" />
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {narrative.scienceModules.map((module) => (
            <ScienceCard
              key={module.id}
              module={module}
              icon={module.id === "neuroplasticity" ? Repeat2 : Sparkles}
            />
          ))}
        </div>
      </section>

      <section className="rounded-[28px] p-6 forr-panel-action sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">Your starting direction</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink">
              {profile.baselineInput.title}
            </h2>
          </div>
          <Lightbulb size={25} className="shrink-0 forr-text-teal-deep" />
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="rounded-[20px] bg-white/75 p-5">
            <div className="forr-overline">What your nervous system may need</div>
            <p className="mt-3 text-sm leading-relaxed forr-text-ink">
              {profile.baselineInput.direction}
            </p>
          </div>
          <div className="rounded-[20px] bg-white/75 p-5">
            <div className="forr-overline">How to begin</div>
            <p className="mt-3 text-sm leading-relaxed forr-text-ink">
              {profile.baselineInput.dose}
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {profile.priorities.map((priority, index) => (
            <article key={priority.title} className="rounded-[20px] bg-white/55 p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] forr-text-teal-deep">
                {index === 0 ? "First" : index === 1 ? "Then" : "Build toward"}
              </div>
              <h3 className="mt-2 text-sm font-semibold forr-text-ink">
                {priority.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed forr-text-soft">
                {priority.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section
        className="rounded-[24px] p-5 forr-surface sm:p-6"
        style={{ border: "1px solid var(--forr-line-soft)" }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="forr-overline">What does not need changing</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              Keep the capacity and supports that already work
            </h2>
          </div>
          <HeartHandshake size={23} className="shrink-0 forr-text-teal-deep" />
        </div>
        <BulletList items={profile.noChangeNeeded} />
      </section>

      <section className="rounded-[24px] p-5 forr-panel-information sm:p-6">
        <div className="flex items-start gap-4">
          <ArrowRight size={22} className="mt-1 shrink-0 forr-text-teal-deep" />
          <div>
            <div className="forr-overline">What happens next</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              Your baseline becomes more useful through observed response
            </h2>
            <p className="mt-3 text-sm leading-relaxed forr-text-soft">
              APOS Companion keeps the technical baseline in the background. Your
              current check-ins, scheduled demands, completed activities, and
              before-to-after responses help it learn what appears useful for you
              under comparable conditions.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RobustAssessmentProfileView;
